/* HR — Leave (R3 fully wired)
 *
 *  Endpoints:
 *    GET    /api/leave/types?active_only=
 *    POST   /api/leave/types                    (HR / admin)
 *    PATCH  /api/leave/types/:id                (HR / admin)
 *    GET    /api/leave/requests?status=&page=
 *    GET    /api/leave/requests/me
 *    POST   /api/leave/requests                 { employee_id, leave_type_id, start_at, end_at, hours?, reason? }
 *    POST   /api/leave/requests/:id/approve     { comment? }
 *    POST   /api/leave/requests/:id/reject      { comment }    (comment required, non-empty)
 *    DELETE /api/leave/requests/:id             (soft cancel)
 *    GET    /api/leave/balances/me?year=
 *    GET    /api/employees/me                   (resolve self employee_id)
 */

/* 後端 leave_types 沒有 color 欄位，UI 顏色由 code → 顏色映射在前端維護。
 * 找不到對應的 code → fallback 到 inkFaint。 */
const LEAVE_TYPE_COLORS = {
  annual:      '#0B5F5A',
  sick:        '#A56A1F',
  personal:    '#8B8F89',
  menstrual:   '#9B3330',
  marriage:    '#A56A1F',
  bereavement: '#4A5048',
  maternity:   '#0B5F5A',
  parental:    '#0B5F5A',
  comp:        '#0B5F5A',
};
function leaveColor(code) {
  // Expressive (GX): the grey "personal" swatch is Bento's inkFaint (#8B8F89, 3.3:1 on white) →
  // use the AA-safe GX faint. All other identity colours are kept as-is (plan §2(d)-5).
  if (code === 'personal') return uiPick(LEAVE_TYPE_COLORS.personal, GxT.color.faint);
  return LEAVE_TYPE_COLORS[code] || HR.inkFaint;
}

/* ── Expressive (GX) skin helpers — plan-expressive-inner-pages.md §2(d).
 * Render-time only; under Bento every helper returns the ORIGINAL object (uiStyle/uiPick),
 * so Bento output is unchanged. Never cache their results at module level. */
// Uppercase / letter-spaced micro label → GX sentence-case 700 label (onV is AA on every tonal surface).
function _lvGxLabel(bento) {
  return uiStyle(bento, { letterSpacing: 0, textTransform: 'none', fontWeight: 700, color: GxT.color.onV });
}
// Thin progress track → GX rounded 6px track on s3.
function _lvGxTrack(bento) {
  return uiStyle(bento, { height: 6, borderRadius: GxT.radius.pill, background: GxT.color.s3 });
}
// Italic serif quote text → GX upright (Figtree has no true italic).
function _lvGxUpright(bento) {
  return uiStyle(bento, { fontStyle: 'normal' });
}

const STATUS_TONES = {
  pending:   { label: '待審核', tone: 'warn',    dot: true },
  approved:  { label: '已核准', tone: 'accent',  dot: true },
  rejected:  { label: '已退回', tone: 'down',    dot: true },
  cancelled: { label: '已取消', tone: 'neutral', dot: true },
};

function StatusPillX({ code }) {
  const s = STATUS_TONES[code] || STATUS_TONES.pending;
  return <Pill tone={s.tone} dot={s.dot}>{s.label}</Pill>;
}

/* 期間顯示：若任一端非整日（HH:MM != 00:00 / 等），就帶上時間。
 * UTC Zulu in → TPE 顯示。*/
// 天數顯示：max 2 位小數、無小數時不顯示 .00；null/undefined 視為 0。
function fmtDays(v) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return '0';
  return Number(n.toFixed(2)).toString();
}

// ISO timestamp → 相對時間（剛剛 / N 分前 / N 小時前 / N 天前 / N 個月前）
function fmtRelativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!t || isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return '剛剛';
  const min = Math.floor(diff / 60000);
  if (min < 1) return '剛剛';
  if (min < 60) return `${min} 分鐘前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小時前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} 個月前`;
  return `${Math.floor(mo / 12)} 年前`;
}

// ISO Zulu → "M/D HH:MM" (TPE)
function fmtShortLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${m}/${day} ${hh}:${mm}`;
}

function fmtPeriod(start, end) {
  if (!start || !end) return '—';
  const s = new Date(start);
  const e = new Date(end);
  const hasTime = (s.getMinutes() !== 0 || s.getHours() !== 0 ||
                   e.getMinutes() !== 0 || (e.getHours() !== 0 && e.getHours() !== 23));
  const fmt = (d, withTime) => withTime ? fmtDateTime(d.toISOString()) : fmtDate(d.toISOString());
  const sStr = fmt(s, hasTime);
  const eStr = fmt(e, hasTime);
  return sStr === eStr ? sStr : `${sStr} ~ ${eStr}`;
}

/* ── 月度統計 helpers（A1/A2/A3）──
 * 月份歸屬一律以 TPE（UTC+8）判斷，避免依賴瀏覽器時區；
 * 做法：epoch + 8h 後取 UTC 年月（等價於 Asia/Taipei 牆鐘）。 */
function tpeMonthKey(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const d = new Date(t + 8 * 3600 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function tpeCurrentMonth() { return tpeMonthKey(new Date().toISOString()); }
// '2026-08' → '2026 年 8 月'
function fmtMonthLabel(m) {
  if (!m || m.length < 7) return m || '';
  return `${m.slice(0, 4)} 年 ${parseInt(m.slice(5, 7), 10)} 月`;
}
// 'YYYY-MM' ± delta 個月
function shiftMonth(m, delta) {
  const d = new Date(Date.UTC(parseInt(m.slice(0, 4), 10), parseInt(m.slice(5, 7), 10) - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function ProofLink({ path }) {
  if (!path) return <span style={{ color: HR.inkFaint }}>—</span>;
  // 專屬 endpoint：嚴格 ACL（HR / 當事員工 / 該員工 manager）+ inline 顯示
  const url = `/api/leave/requests/proof?path=${encodeURIComponent(path)}&token=${encodeURIComponent(getToken() || '')}`;
  const raw = path.split('/').pop() || '';
  // upload 時前綴了 timestamp（20260519T142500Z_），顯示時拿掉只留原檔名
  const name = raw.replace(/^\d{8}T\d{6}Z_/, '');
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       style={{ color: HR.accent, fontSize: FontSizes.xxs, fontFamily: HR.mono, textDecoration: 'underline' }}
       title={raw}>
      📎 {name.length > 20 ? name.slice(0, 18) + '…' : name}
    </a>
  );
}

/* ─────────────────────────  Apply form  ───────────────────
 * 日期（date input）＋時間（TimeSelect，15 分鐘刻度）四欄，送出時組回
 * 'YYYY-MM-DDTHH:MM'；後端會把 naive 字串當 TPE 換成 UTC Zulu。
 * requires_proof=true 的假別 → 證明文件必填，先 POST /leave/requests/proof-upload
 * 取回 path，再帶到 POST /leave/requests。
 */

// 台北當日 'YYYY-MM-DD'。不可用 toISOString()（UTC），否則台北 00:00–08:00
// 開單時預設會帶到「昨天」，使用者未改就送出會把假請成前一天。
function _tpeTodayYmd() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
}

async function uploadProofFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const token = getToken();
  const res = await fetch('/api/leave/requests/proof-upload', {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = (typeof data?.detail === 'string') ? data.detail : `HTTP ${res.status}`;
    throw { status: res.status, detail };
  }
  return data;  // { path, filename, size }
}

function ApplyLeaveForm({ types, selfEmpId, hoursPerDay, onCancel, onSaved }) {
  const toast = useToast();
  const activeTypes = types.filter(t => t.active);
  // 預設假別優先選「事假」（code === 'personal'），找不到才退回清單第一筆
  const [typeId, setTypeId] = React.useState(
    activeTypes.find(t => t.code === 'personal')?.id ?? activeTypes[0]?.id ?? null
  );
  // 日期 + 時間分四欄（時間用 TimeSelect，15 分鐘刻度）；送出時組回 'YYYY-MM-DDTHH:MM'，
  // payload 與後端介面不變（後端把 naive 字串視為 TPE）。日期預設 TPE 今天；
  // 時間預設 09:00/18:00，排班（schedules/me）載入後換成今天 weekday 的排班時間。
  const [fromDate, setFromDate] = React.useState(_tpeTodayYmd());
  const [fromTime, setFromTime] = React.useState('09:00');
  const [toDate, setToDate] = React.useState(_tpeTodayYmd());
  const [toTime, setToTime] = React.useState('18:00');
  const [reason, setReason] = React.useState('');

  // 員工本人排班 — GET /attendance/schedules/me（個人覆寫優先，公司預設補缺 weekday；
  // 回傳每 weekday 各一筆 { weekday: 1..7(一..日), start_time, end_time }）。
  // 供快捷鈕與預設時間帶入；該 weekday 無排班（休息日）或 API 失敗 → fallback 09:00–18:00。
  const [mySchedules, setMySchedules] = React.useState(null);  // null = 尚未載入
  React.useEffect(() => {
    let alive = true;
    api('GET', '/attendance/schedules/me')
      .then(rows => { if (alive) setMySchedules(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setMySchedules([]); });
    return () => { alive = false; };
  }, []);

  // 'YYYY-MM-DD' → ISO weekday (1=一..7=日)；無效日期回 null
  const _isoWeekday = (ymd) => {
    const d = new Date(`${ymd}T00:00:00`);
    if (!ymd || Number.isNaN(d.getTime())) return null;
    return d.getDay() === 0 ? 7 : d.getDay();
  };
  const _schedForDate = (ymd) => {
    const wd = _isoWeekday(ymd);
    if (!wd || !mySchedules) return null;
    return mySchedules.find(s => s.weekday === wd) || null;
  };

  // 使用者手動改過時間欄後，排班載入就不再覆蓋預設值
  const timesTouchedRef = React.useRef(false);
  const changeFromTime = (v) => { timesTouchedRef.current = true; setFromTime(v); };
  const changeToTime = (v) => { timesTouchedRef.current = true; setToTime(v); };

  // 排班載入後，把預設 09:00/18:00 換成「今天 weekday」的排班時間
  React.useEffect(() => {
    if (!mySchedules || timesTouchedRef.current) return;
    const s = mySchedules.find(x => x.weekday === _isoWeekday(_tpeTodayYmd()));
    if (s?.start_time) setFromTime(s.start_time);
    if (s?.end_time) setToTime(s.end_time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySchedules]);
  const [proofFile, setProofFile] = React.useState(null);
  const [proofPath, setProofPath] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const selectedType = activeTypes.find(t => t.id === typeId);
  const needsProof = !!selectedType?.requires_proof;

  // 送出用的完整起訖字串（四欄組回，任一欄空 → 空字串擋在驗證）
  const from = (fromDate && fromTime) ? `${fromDate}T${fromTime}` : '';
  const to = (toDate && toTime) ? `${toDate}T${toTime}` : '';

  // 快捷時段（樣式比照補打卡 modal 的 punchPresets 膠囊鈕）——只改時間欄、不動日期欄。
  // 依「開始日期欄位」當時 weekday 的本人排班帶時間（開始日期變更即重算）；
  // 該 weekday 無排班或排班未載入 → fallback 09:00–18:00。
  // 午休 12:00–12:30 屬公司固定設定 → 下半天從 12:30 開始。
  const fromSched = _schedForDate(fromDate);
  const workStart = fromSched?.start_time || '09:00';
  const workEnd = fromSched?.end_time || '18:00';
  const leavePresets = [
    { key: 'full', label: '全天', from: workStart, to: workEnd },
    { key: 'am', label: '上半天', from: workStart, to: '12:00' },
    { key: 'pm', label: '下半天', from: '12:30', to: workEnd },
  ];
  const applyLeavePreset = (p) => { changeFromTime(p.from); changeToTime(p.to); };
  const activeLeavePreset = leavePresets.find(p => p.from === fromTime && p.to === toTime)?.key;

  // 時長計算（本地估算，與後端保持一致）
  const hpd = hoursPerDay || 8;
  const startMs = new Date(from).getTime();
  const endMs = new Date(to).getTime();
  const diffHours = endMs > startMs ? (endMs - startMs) / 3600000 : 0;
  const diffDays = diffHours / hpd;

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast('檔案上限 5 MB', 'error'); return; }
    setProofFile(f);
    setUploading(true);
    try {
      const r = await uploadProofFile(f);
      setProofPath(r.path);
      toast('證明文件已上傳', 'ok');
    } catch (err) {
      setProofFile(null);
      toast('上傳失敗：' + (err.detail || err.message || ''), 'error');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!typeId) { toast('請選擇假別', 'error'); return; }
    if (!from || !to) { toast('請填寫起訖時間', 'error'); return; }
    if (endMs <= startMs) { toast('結束時間需晚於開始時間', 'error'); return; }
    if (!selfEmpId) { toast('找不到您的員工記錄，請聯絡 HR 建立', 'error'); return; }
    if (needsProof && !proofPath) { toast(`「${selectedType.name}」需附證明文件`, 'error'); return; }
    setSubmitting(true);
    try {
      await api('POST', '/leave/requests', {
        employee_id: selfEmpId,
        leave_type_id: typeId,
        start_at: from,  // 'YYYY-MM-DDTHH:MM' — 後端視為 TPE → 轉 UTC Zulu
        end_at: to,
        hours: null,     // 時長以 datetime 為單一事實來源
        reason: reason.trim() || null,
        proof_file_path: proofPath || null,
      });
      toast('申請已送出', 'ok');
      onSaved();
    } catch (e) {
      toast('送出失敗：' + (e.detail || e.message || ''), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="新增申請"
      subtitle="New Request"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting || uploading}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || uploading}>
            {submitting ? '送出中…' : '送出申請'}
          </Button>
        </>
      }
    >
      <div className="hr-leave-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="假別" required span={2}>
          <Select
            value={typeId}
            onChange={(v) => setTypeId(v ? parseInt(v) : null)}
            allowEmpty={false}
            options={activeTypes.map(t => ({ value: t.id, label: t.name + (t.requires_proof ? ' ⚠需證明' : '') }))}
          />
        </Field>
        {/* 快捷時段膠囊鈕 — 只改時間欄、不動日期欄 */}
        <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={_lvGxLabel({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' })}>快捷</span>
          {leavePresets.map(p => {
            const on = activeLeavePreset === p.key;
            return (
              <button key={p.key} type="button" onClick={() => applyLeavePreset(p)}
                aria-pressed={uiPick(undefined, on)}
                style={uiStyle({
                  padding: '5px 12px', borderRadius: 20, cursor: 'pointer',
                  fontSize: FontSizes.xxs, fontWeight: 600,
                  border: `1px solid ${on ? HR.accent : HR.rule}`,
                  background: on ? HR.accentSoft : HR.surface,
                  color: on ? HR.accent : HR.inkSoft,
                  transition: 'background .12s, border-color .12s, color .12s',
                }, {
                  // M3 filter chip
                  minHeight: 32, padding: '0 14px', borderRadius: GxT.radius.chip, fontWeight: 700,
                  border: `1px solid ${on ? 'transparent' : HR.rule}`,
                  background: on ? GxT.color.sc : GxT.color.card,
                  color: on ? GxT.color.onSc : GxT.color.onV,
                })}
                onMouseEnter={(e) => { if (!on) { e.currentTarget.style.borderColor = HR.accent; e.currentTarget.style.color = HR.accent; } }}
                onMouseLeave={(e) => { if (!on) { e.currentTarget.style.borderColor = HR.rule; e.currentTarget.style.color = HR.inkSoft; } }}
              >{p.label}</button>
            );
          })}
        </div>
        <Field label="開始日期" required>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle({ fontFamily: HR.mono })} />
        </Field>
        <Field label="開始時間" required hint="台北時區，15 分鐘刻度">
          <TimeSelect value={fromTime} onChange={changeFromTime} />
        </Field>
        <Field label="結束日期" required>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle({ fontFamily: HR.mono })} />
        </Field>
        <Field label="結束時間" required hint="台北時區，15 分鐘刻度">
          <TimeSelect value={toTime} onChange={changeToTime} />
        </Field>
        <Field label="事由" hint="簡述請假原因" span={2}>
          <Textarea value={reason} onChange={setReason} rows={3} placeholder="…" />
        </Field>

        {/* 證明文件 — requires_proof=true 時必填，紅框警示 */}
        <Field
          label={`證明文件${needsProof ? ' *' : ''}`}
          hint={needsProof ? '此假別必附（pdf/jpg/png，上限 5MB）' : '選填（pdf/jpg/png，上限 5MB）'}
          span={2}
        >
          <div style={uiStyle({
            padding: 10, border: `1px ${needsProof && !proofPath ? 'solid' : 'dashed'} ${needsProof && !proofPath ? HR.down : HR.rule}`,
            borderRadius: 4, background: needsProof && !proofPath ? '#FAEFEF' : HR.surfaceAlt,
          }, {
            padding: 12, borderRadius: GxT.radius.inner,
            background: needsProof && !proofPath ? GxT.tint.down.bg : GxT.color.s1,
          })}>
            {proofPath ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: FontSizes.trading_md }}>
                <span style={{ fontFamily: HR.mono, color: HR.accent }}>
                  ✓ {proofFile?.name || proofPath.split('/').pop()}
                </span>
                <Button size="sm" variant="ghost" onClick={() => { setProofPath(null); setProofFile(null); }} style={{ color: HR.down }}>
                  移除
                </Button>
              </div>
            ) : (
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleFileChange}
                disabled={uploading}
                style={{ fontSize: FontSizes.trading_md, width: '100%' }}
              />
            )}
            {uploading && <div style={{ marginTop: 6, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>上傳中…</div>}
          </div>
        </Field>

        <div style={uiStyle({
          gridColumn: 'span 2', padding: 12, background: HR.surfaceAlt, borderRadius: 4,
          fontSize: FontSizes.xxs, color: HR.inkSoft, fontFamily: HR.mono, lineHeight: 1.6,
        }, { padding: '12px 14px', background: GxT.color.s2, borderRadius: GxT.radius.inner })}>
          時長：<strong style={{ color: HR.ink }}>{diffHours.toFixed(2)}</strong> 小時 ·
          約 <strong style={{ color: HR.ink }}>{diffDays.toFixed(2)}</strong> 天
          {hpd !== 8 && <> <span style={{ color: HR.inkFaint }}>（您每日工時 {hpd}h）</span></>} ·
          假別：<strong style={{ color: leaveColor(selectedType?.code) }}>{selectedType?.name || '—'}</strong>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  Decision (approve / reject) modal ───── */

function DecisionModal({ req, action, onClose, onSaved }) {
  const toast = useToast();
  const [comment, setComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const isReject = action === 'reject';

  const submit = async () => {
    if (isReject && !comment.trim()) { toast('退回需填寫理由', 'error'); return; }
    setSubmitting(true);
    try {
      const body = isReject ? { comment: comment.trim() } : { comment: comment.trim() || null };
      await api('POST', `/leave/requests/${req.id}/${action}`, body);
      toast(`${isReject ? '已退回' : '已核准'} ${req.employee_name || '#' + req.employee_id} 的${req.leave_type_name || ''}`, isReject ? 'info' : 'ok');
      onSaved();
    } catch (e) {
      toast(`${isReject ? '退回' : '核准'}失敗：` + (e.detail || e.message || ''), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={isReject ? `退回申請` : `核准申請`}
      subtitle={req.request_no}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>取消</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={submitting}
            style={isReject ? { background: HR.down, boxShadow: '0 1px 0 rgba(155,51,48,0.3)' } : undefined}
          >
            {submitting ? '處理中…' : (isReject ? '確認退回' : '確認核准')}
          </Button>
        </>
      }
    >
      <div style={uiStyle({ marginBottom: 14, padding: 12, background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.trading_md, color: HR.inkSoft, lineHeight: 1.6 }, { padding: '12px 14px', background: GxT.color.s2, borderRadius: GxT.radius.inner })}>
        <div><strong style={{ color: HR.ink }}>{req.employee_name || `#${req.employee_id}`}</strong> · {req.leave_type_name}</div>
        <div style={{ fontFamily: HR.mono, marginTop: 2 }}>
          {fmtPeriod(req.start_at, req.end_at)}
          {' · '}{(req.duration_days ?? 0).toFixed(2)} 天
        </div>
        {req.reason && <div style={_lvGxUpright({ marginTop: 6, fontStyle: 'italic', fontFamily: HR.serif })}>「{req.reason}」</div>}
        {req.proof_file_path && (
          <div style={{ marginTop: 6 }}>證明文件：<ProofLink path={req.proof_file_path} /></div>
        )}
      </div>
      <Field label={isReject ? '退回理由' : '核准備註'} required={isReject}>
        <Textarea value={comment} onChange={setComment} rows={3} placeholder={isReject ? '請填寫退回原因…' : '（選填）'} />
      </Field>
    </Modal>
  );
}

/* ─────────────────────────  我的請假  ───────────────────────── */

function MyLeaveTab({ types, myBalances, myRequests, selfEmpId, onRefresh }) {
  const toast = useToast();
  const [openForm, setOpenForm] = React.useState(false);
  const [cancelTarget, setCancelTarget] = React.useState(null);
  const [cancelling, setCancelling] = React.useState(false);

  /* ── A2/A3：統計卡月份（TPE）── 預設當月；切換非當月時重打 balances/me?month= */
  const currentMonth = tpeCurrentMonth();
  const [statMonth, setStatMonth] = React.useState(currentMonth);
  const [monthBalances, setMonthBalances] = React.useState(null);  // null = 用 props（當月資料）

  React.useEffect(() => {
    if (statMonth === currentMonth) { setMonthBalances(null); return undefined; }
    let alive = true;
    api('GET', `/leave/balances/me?year=${statMonth.slice(0, 4)}&month=${statMonth}`)
      .then(rows => { if (alive) setMonthBalances(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setMonthBalances(null); });
    return () => { alive = false; };
  }, [statMonth, currentMonth, myBalances]);  // myBalances 變動（onRefresh）時同步重抓非當月統計

  const balances = monthBalances != null ? monthBalances : myBalances;

  /* 統計卡固定三張：特休 / 事假 / 補班，依此順序。
   * 其他 balance 列（隔月補班 QQ、國定假日補休、國定連假、公假、喪假、調休等）不出卡片；
   * 純前端過濾，不影響清單月份過濾與月份切換器。 */
  const CARD_TYPE_CODES = ['annual', 'personal', 'makeup'];
  const cardBalances = CARD_TYPE_CODES
    .map(code => balances.find(b => b.leave_type_code === code))
    .filter(Boolean);

  /* A1：清單只顯示統計月份（statMonth）當月的紀錄 —
   * 以假期實際發生日（start_at 的 TPE 月份）歸月，跨月申請歸起始月（列上保留「跨月」徽章） */
  const monthRequests = React.useMemo(
    () => myRequests.filter(r => tpeMonthKey(r.start_at) === statMonth),
    [myRequests, statMonth],
  );

  /* 切換器邊界：有紀錄的最早月 ～ max(已有申請的未來月, 當月) */
  const monthBounds = React.useMemo(() => {
    const keys = myRequests.map(r => tpeMonthKey(r.start_at)).filter(Boolean);
    keys.push(currentMonth);
    keys.sort();
    return { min: keys[0], max: keys[keys.length - 1] };
  }, [myRequests, currentMonth]);

  // A3：切換月份 → 卡片（effect 重打 balances/me?month=）與清單（monthRequests 過濾）同步變
  const gotoMonth = (m) => setStatMonth(m);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api('DELETE', `/leave/requests/${cancelTarget.id}`);
      toast('已取消', 'ok');
      setCancelTarget(null);
      onRefresh();
    } catch (e) {
      toast('取消失敗：' + (e.detail || ''), 'error');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      {/* A3: 統計卡月份切換器 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={_lvGxLabel({ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint })}>
          統計月份
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Button size="sm" variant="ghost" disabled={statMonth <= monthBounds.min}
            onClick={() => gotoMonth(shiftMonth(statMonth, -1))} title="上一月">‹</Button>
          <span style={uiStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs, color: HR.ink, minWidth: 96, textAlign: 'center' }, { fontFamily: GxT.font, fontWeight: 800, fontVariantNumeric: 'tabular-nums' })}>
            {fmtMonthLabel(statMonth)}
          </span>
          <Button size="sm" variant="ghost" disabled={statMonth >= monthBounds.max}
            onClick={() => gotoMonth(shiftMonth(statMonth, 1))} title="下一月">›</Button>
          {statMonth !== currentMonth && (
            <Button size="sm" variant="ghost" onClick={() => gotoMonth(currentMonth)}>回本月</Button>
          )}
        </div>
      </div>

      {/* Balance cards */}
      <div className="rwd-grid-3 hr-leave-balance-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {cardBalances.length === 0 ? (
          <div style={uiStyle({
            gridColumn: '1 / -1', padding: 24, textAlign: 'center',
            color: HR.inkFaint, fontSize: FontSizes.xs, background: HR.surface,
            border: `1px solid ${HR.rule}`, borderRadius: 6,
          }, { border: 'none', borderRadius: GxT.radius.card, color: GxT.color.onV })}>
            尚未設定今年度的請假額度 — 請聯絡 HR 建立。
          </div>
        ) : cardBalances.map(b => {
          // 1. 補班 (makeup) 假別且為配額制：專屬小時卡片
          if (b.leave_type_code === 'makeup' && b.quota_managed !== false) {
            const mQuota = b.makeup_quota_hours ?? 0;   // 當月請假須補（該月 approved full 假時數）
            const mUsedH = b.month_used_hours;          // 該月已核准補班；null = fallback 舊顯示（防呆）
            const mPendH = b.month_pending_hours || 0;
            const owed = b.month_owed_hours;            // 還要補 = 須補 − 已核准補班（≥0）
            const mLabel = fmtMonthLabel(b.month || statMonth) || '本月';
            if (mUsedH != null) {
              // 以「補滿當月請假時數」為目標的進度卡（與其他三張卡的當月語意對稱）
              const pct = mQuota > 0 ? Math.min(100, Math.round((mUsedH / mQuota) * 100)) : 0;
              return (
                <Card key={b.id} eyebrow={b.leave_type_name || b.leave_type_code}
                  title={`${mUsedH.toFixed(1)}`} italic={`已核准（小時）· ${mLabel}`}>
                  <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginBottom: 8, fontFamily: HR.mono }}>
                    當月請假須補 {mQuota.toFixed(1)} H ·{' '}
                    {(owed != null && owed > 0)
                      ? <>還要補 <strong style={{ color: HR.ink }}>{owed.toFixed(1)} H</strong></>
                      : '已補完'}
                    {mPendH > 0 && <> · 待審 {mPendH.toFixed(1)} H</>}
                  </div>
                  <div style={_lvGxTrack({ height: 4, background: HR.ruleSoft, borderRadius: 2, overflow: 'hidden' })}>
                    <div style={{ width: `${pct}%`, height: '100%', background: leaveColor(b.leave_type_code), transition: 'width 200ms' }} />
                  </div>
                </Card>
              );
            }
            // fallback：後端未回月度欄位 → 原「剩餘可補」顯示
            const mRemaining = b.makeup_remaining_hours ?? 0;
            const mUsedTotal = (b.used_hours || 0) + (b.pending_hours || 0);
            const pct = mQuota > 0 ? Math.min(100, Math.round((mUsedTotal / mQuota) * 100)) : 0;
            return (
              <Card key={b.id} eyebrow={b.leave_type_name || b.leave_type_code}
                title={`${mRemaining.toFixed(1)}`} italic="剩餘可補（小時）">
                <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginBottom: 8, fontFamily: HR.mono }}>
                  {mLabel}可補 {mQuota.toFixed(1)} H · 已申請 {mUsedTotal.toFixed(1)} H
                  {(b.pending_hours || 0) > 0 && <> (待審 {b.pending_hours.toFixed(1)} H)</>}
                </div>
                <div style={_lvGxTrack({ height: 4, background: HR.ruleSoft, borderRadius: 2, overflow: 'hidden' })}>
                  <div style={{ width: `${pct}%`, height: '100%', background: leaveColor(b.leave_type_code), transition: 'width 200ms' }} />
                </div>
              </Card>
            );
          }

          // 2. 一般事假/病假（quota_managed=false 且非補班）：主數字顯示「該月已核准時數」
          //    （後端 month_used_hours，跨月單按實際發生日分攤）；年度累計移到說明列。
          if (b.quota_managed === false) {
            const usedH = b.used_hours || 0;
            const mUsed = b.month_used_hours;          // null = 後端未回月度統計（fallback 年度）
            const mPending = b.month_pending_hours || 0;
            const mLabel = fmtMonthLabel(b.month || statMonth);
            return (
              <Card key={b.id} eyebrow={b.leave_type_name || b.leave_type_code}
                title={`${(mUsed != null ? mUsed : usedH).toFixed(1)}`}
                italic={mUsed != null ? `已核准（小時）· ${mLabel}` : '已使用（小時）'}>
                <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono }}>
                  {mUsed != null
                    ? <>統計區間 {mLabel} · 年度累計 {usedH.toFixed(1)} H</>
                    : <>此類別不適用配額管理 · 用多少算多少</>}
                  {mUsed != null
                    ? (mPending > 0 && <><br />該月待審 {mPending.toFixed(1)} 小時</>)
                    : ((b.pending_hours || 0) > 0 && <><br />待審 {b.pending_hours.toFixed(1)} 小時</>)}
                </div>
              </Card>
            );
          }

          // 3. 配額制（特休等）：主數字 = 該月已核准天數（month_used_days，
          //    跟月份切換器變）；年度餘額/額度降級到說明列。後端未回月度欄位時 fallback 年度餘額。
          const used = b.used_days || 0;
          const granted = (b.entitled_days || 0) + (b.adjusted_days || 0);
          const pct = granted > 0 ? Math.min(100, Math.round((used / granted) * 100)) : 0;
          const mUsedD = b.month_used_days;        // null = fallback 年度顯示（防呆）
          const mPendD = b.month_pending_days || 0;
          const qLabel = fmtMonthLabel(b.month || statMonth);
          return (
            <Card key={b.id} eyebrow={b.leave_type_name || b.leave_type_code}
              title={mUsedD != null ? `${mUsedD.toFixed(2)}` : `${(b.available_days ?? 0).toFixed(1)}`}
              italic={mUsedD != null ? `已核准（天）· ${qLabel}` : '餘額（天）'}>
              <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginBottom: 8, fontFamily: HR.mono }}>
                {mUsedD != null
                  ? <>年度剩餘 {(b.available_days ?? 0).toFixed(1)} 天 / 額度 {granted.toFixed(1)} 天</>
                  : <>已用 {used.toFixed(1)} / 核給 {granted.toFixed(1)}</>}
                {mUsedD != null
                  ? (mPendD > 0 && <> · 待審 {mPendD.toFixed(2)} 天</>)
                  : ((b.pending_days || 0) > 0 && <> · 待審 {b.pending_days.toFixed(1)}</>)}
              </div>
              <div style={_lvGxTrack({ height: 4, background: HR.ruleSoft, borderRadius: 2, overflow: 'hidden' })}>
                <div style={{ width: `${pct}%`, height: '100%', background: leaveColor(b.leave_type_code), transition: 'width 200ms' }} />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Requests table */}
      <Card className="hr-leave-requests-card" padding="0" eyebrow="My Requests" title="我的假勤紀錄"
        italic={`${monthRequests.length} 筆（${fmtMonthLabel(statMonth)}）`} right={
        <Button variant="primary" size="md" icon="plus" onClick={() => setOpenForm(true)} disabled={!selfEmpId}>
          新增申請
        </Button>
      }>
        {monthRequests.length === 0 ? (
          myRequests.length === 0 ? (
            <EmptyState icon="calendar" title="尚無申請紀錄" hint={selfEmpId ? '點右上角開始申請（請假 / 加班 / 補班）。' : '您尚未對應員工記錄，請聯絡 HR。'} />
          ) : (
            <EmptyState icon="calendar" title={`${fmtMonthLabel(statMonth)} 無申請紀錄`} hint="用上方月份切換器查看其他月份。" />
          )
        ) : (
          <div className="va2-table-wrapper">
            <table className="hr-leave-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>假別</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>期間</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>天數</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>事由</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>附件</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>審核</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {monthRequests.map(r => {
                const cancellable = r.status === 'pending' || r.status === 'approved';
                const crossMonth = tpeMonthKey(r.end_at) !== tpeMonthKey(r.start_at);
                return (
                  <tr key={r.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                    <td style={{ padding: '13px 16px 13px 24px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: FontSizes.xxs, fontWeight: 500, color: leaveColor(r.leave_type_code),
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: uiPick(1, '50%'), background: leaveColor(r.leave_type_code) }} />
                        {r.leave_type_name || r.leave_type_code || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
                      {fmtPeriod(r.start_at, r.end_at)}
                      {crossMonth && (
                        <span style={uiStyle({
                          marginLeft: 6, padding: '1px 5px', borderRadius: 3,
                          border: `1px solid ${HR.rule}`, color: HR.inkFaint,
                          fontSize: FontSizes.trading_lg, fontFamily: HR.sans, whiteSpace: 'nowrap',
                        }, { padding: '2px 8px', borderRadius: GxT.radius.chipSm, border: 'none', background: GxT.color.s2, color: GxT.color.onV, fontWeight: 700 })}>跨月</span>
                      )}
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{(r.duration_days ?? 0).toFixed(2)}d</td>
                    <td style={_lvGxUpright({ padding: '13px 16px', color: HR.inkSoft, fontStyle: r.reason ? 'italic' : 'normal', fontFamily: HR.serif, fontSize: FontSizes.xs })}>
                      {r.reason || '—'}
                    </td>
                    <td style={{ padding: '13px 16px' }}><ProofLink path={r.proof_file_path} /></td>
                    <td style={{ padding: '13px 16px' }}><StatusPillX code={r.status} /></td>
                    <td style={{ padding: '13px 16px', fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                      {r.decided_by_name ? (
                        <>
                          <div>{r.decided_by_name}</div>
                          {r.decision_remark && <div style={_lvGxUpright({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: 'italic', marginTop: 2 })}>「{r.decision_remark}」</div>}
                        </>
                      ) : <span style={{ color: HR.inkFaint }}>等候中</span>}
                    </td>
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                      {cancellable && (
                        <Button size="sm" variant="ghost" onClick={() => setCancelTarget(r)} style={{ color: HR.down }}>取消</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      {openForm && (
        <ApplyLeaveForm
          types={types} selfEmpId={selfEmpId}
          hoursPerDay={myBalances[0]?.hours_per_day || 8}
          onCancel={() => setOpenForm(false)}
          onSaved={() => { setOpenForm(false); onRefresh(); }}
        />
      )}
      <ConfirmDialog
        open={!!cancelTarget}
        title="取消申請"
        message={cancelTarget ? `確定取消「${cancelTarget.leave_type_name || ''}」於 ${fmtDate(cancelTarget.start_at)} 的申請嗎？${cancelTarget.status === 'approved' ? '此申請已核准，取消後將退還已使用天數。' : ''}` : ''}
        danger
        busy={cancelling}
        onCancel={() => setCancelTarget(null)}
        onConfirm={doCancel}
      />
    </>
  );
}

/* ─────────────────────────  審核佇列  ───────────────────────── */

function ApprovalsTab({ pendingRequests, onRefresh }) {
  const [decision, setDecision] = React.useState(null);  // { req, action }

  return (
    <>
      <Card padding="0" eyebrow="Approval Queue" title="待審核項目" italic={`${pendingRequests.length} 筆`}>
        {pendingRequests.length === 0 ? (
          <EmptyState icon="calendar" title="目前沒有待審核項目" hint="所有申請都已處理完畢。" />
        ) : (
          <div>
            {pendingRequests.map(r => (
              <div key={r.id} className="hr-leave-approval-item" style={{
                display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 16,
                padding: '18px 24px', borderTop: `1px solid ${HR.ruleSoft}`, alignItems: 'center',
              }}>
                <div style={uiStyle({
                  width: 36, height: 36, borderRadius: '50%', background: '#D6CEB9',
                  display: 'grid', placeItems: 'center', fontSize: FontSizes.xxs, fontWeight: 600,
                }, { background: GxT.color.tc, color: GxT.color.onTc, fontWeight: 800 })}>{(r.employee_name || '？').slice(0, 1)}</div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: uiPick(500, 700) }}>{r.employee_name || <span style={{ color: HR.inkFaint, fontWeight: 400 }}>—</span>}</span>
                    <span style={uiStyle({
                      fontSize: FontSizes.xxs, fontWeight: 500, color: leaveColor(r.leave_type_code),
                      padding: '2px 8px', background: GxCore.alpha(leaveColor(r.leave_type_code), '15'), borderRadius: 3,
                    }, { fontWeight: 700, padding: '3px 10px', borderRadius: GxT.radius.chipSm })}>{r.leave_type_name}</span>
                    <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono }}>
                      {fmtPeriod(r.start_at, r.end_at)}
                      {' · '}{(r.duration_days ?? 0).toFixed(2)} 天
                    </span>
                    {r.proof_file_path && <ProofLink path={r.proof_file_path} />}
                  </div>
                  <div style={_lvGxUpright({ fontSize: FontSizes.xs, color: HR.inkSoft, fontFamily: HR.serif, fontStyle: r.reason ? 'italic' : 'normal' })}>
                    {r.reason || '（未填事由）'}
                  </div>
                  <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 4, fontFamily: HR.mono }}>
                    {r.request_no} · 申請於 {fmtDateTime(r.submitted_at || r.created_at)}
                  </div>
                </div>
                <div className="hr-leave-approval-actions" style={{ display: 'flex', gap: 6 }}>
                  <Button size="sm" variant="ghost" onClick={() => setDecision({ req: r, action: 'reject' })} style={{ color: HR.down }}>退回</Button>
                  <Button size="sm" variant="primary" onClick={() => setDecision({ req: r, action: 'approve' })}>核准</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {decision && (
        <DecisionModal
          req={decision.req}
          action={decision.action}
          onClose={() => setDecision(null)}
          onSaved={() => { setDecision(null); onRefresh(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  假別管理（HR）  ───────────────────── */

function TypeForm({ initial, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = !!initial?.id;
  const [code, setCode] = React.useState(initial?.code || '');
  const [name, setName] = React.useState(initial?.name || '');
  const [paid, setPaid] = React.useState(initial?.paid ?? true);
  const [requires_proof, setRequiresProof] = React.useState(initial?.requires_proof ?? false);
  const [admin_only, setAdminOnly] = React.useState(initial?.admin_only ?? false);
  const [quota_managed, setQuotaManaged] = React.useState(initial?.quota_managed ?? true);
  const [active, setActive] = React.useState(initial?.active ?? true);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!code.trim() || !name.trim()) { toast('代碼與名稱必填', 'error'); return; }
    setSubmitting(true);
    try {
      const body = { code: code.trim(), name: name.trim(), paid, requires_proof, active, admin_only, quota_managed };
      if (isEdit) await api('PATCH', `/leave/types/${initial.id}`, body);
      else        await api('POST',  '/leave/types', body);
      toast(isEdit ? '已更新' : '已新增', 'ok');
      onSaved();
    } catch (e) {
      toast((isEdit ? '更新' : '新增') + '失敗：' + (e.detail || ''), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={isEdit ? `編輯假別 — ${initial.name}` : '新增假別'}
      subtitle="Leave Type"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '儲存中…' : (isEdit ? '儲存' : '新增')}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="代碼" required hint="英文小寫，例：annual / sick">
          <TextInput value={code} onChange={setCode} mono />
        </Field>
        <Field label="名稱" required>
          <TextInput value={name} onChange={setName} />
        </Field>
        <Field label="有薪">
          <select value={paid ? '1' : '0'} onChange={(e) => setPaid(e.target.value === '1')} style={inputStyle()}>
            <option value="1">是</option>
            <option value="0">否</option>
          </select>
        </Field>
        <Field label="需證明文件">
          <select value={requires_proof ? '1' : '0'} onChange={(e) => setRequiresProof(e.target.value === '1')} style={inputStyle()}>
            <option value="0">否</option>
            <option value="1">是</option>
          </select>
        </Field>
        <Field label="管理員專用" hint="啟用後一般員工請假不可見與選取">
          <select value={admin_only ? '1' : '0'} onChange={(e) => setAdminOnly(e.target.value === '1')} style={inputStyle()}>
            <option value="0">否</option>
            <option value="1">是</option>
          </select>
        </Field>
        <Field label="額度管理" hint="啟用後會為員工發放並管控年度額度">
          <select value={quota_managed ? '1' : '0'} onChange={(e) => setQuotaManaged(e.target.value === '1')} style={inputStyle()}>
            <option value="1">是</option>
            <option value="0">否</option>
          </select>
        </Field>
        <Field label="啟用" span={2}>
          <select value={active ? '1' : '0'} onChange={(e) => setActive(e.target.value === '1')} style={inputStyle()}>
            <option value="1">啟用中</option>
            <option value="0">已停用</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function TypesTab({ types, onRefresh }) {
  const [editing, setEditing] = React.useState(null);  // null = closed, {} = new, row = edit

  return (
    <>
      <Card className="hr-leave-types-card" padding="0" eyebrow="Leave Types" title="假別設定" italic={`${types.length} 種`} right={
        <Button variant="primary" size="md" icon="plus" onClick={() => setEditing({})}>新增假別</Button>
      }>
        {types.length === 0 ? (
          <EmptyState icon="settings" title="尚未建立假別" hint="點右上角新增。" />
        ) : (
          <div className="hr-leave-types-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>代碼</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>名稱</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>有薪</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>需證明</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>額度管理</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>管理員專用</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: leaveColor(t.code), border: `1px solid ${HR.rule}` }} />
                      {t.code}
                    </span>
                  </td>
                  <td style={{ padding: '13px 16px', fontWeight: 500 }}>{t.name}</td>
                  <td style={{ padding: '13px 16px' }}>
                    {t.paid ? <Pill tone="accent" dot>有薪</Pill> : <Pill tone="neutral">無薪</Pill>}
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    {t.requires_proof ? <Pill tone="warn" dot>需附</Pill> : <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>不需</span>}
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    {t.quota_managed ? <Pill tone="accent" dot>配額制</Pill> : <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>不限額</span>}
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    {t.admin_only ? <Pill tone="warn" dot>管理員專用</Pill> : <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>一般員工</span>}
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    {t.active ? <Pill tone="accent" dot>啟用中</Pill> : <Pill tone="neutral">已停用</Pill>}
                  </td>
                  <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>編輯</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
      {editing !== null && (
        <TypeForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  Balances admin (HR / admin only)  ─────────────────────
 *  Lists every employee × leave_type × year balance, lets HR adjust
 *  entitled_days / adjusted_days (the human-controlled fields). used_days and
 *  pending_days are computed from leave_requests and shown read-only.
 *  Backend: GET /leave/balances · POST /leave/balances · PATCH /leave/balances/:id
 *  · POST /leave/balances/recompute (recalc used/pending from requests).
 *
 *  Tab is gated on `leave.type.manage` capability (same as 假別管理), since
 *  granting entitlements is a similarly-HR-scoped admin action.
 */
function BalanceForm({ initial, types, employees, defaultYear, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = initial && initial.id;
  const [employeeId, setEmployeeId] = React.useState(initial?.employee_id || (employees[0]?.id ?? null));
  const [leaveTypeId, setLeaveTypeId] = React.useState(initial?.leave_type_id || (types.find(t => t.active && t.quota_managed)?.id ?? null));
  const [year, setYear] = React.useState(initial?.year || defaultYear || new Date().getFullYear());
  const [entitled, setEntitled] = React.useState(String(initial?.entitled_days ?? 0));
  const [adjusted, setAdjusted] = React.useState(String(initial?.adjusted_days ?? 0));
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    const entitledNum = parseFloat(entitled);
    const adjustedNum = parseFloat(adjusted);
    if (!Number.isFinite(entitledNum) || entitledNum < 0) { toast('已給天數需為 ≥ 0 的數字', 'error'); return; }
    if (!Number.isFinite(adjustedNum))                    { toast('調整天數需為數字', 'error'); return; }
    if (!isEdit) {
      if (!employeeId)  { toast('請選擇員工', 'error'); return; }
      if (!leaveTypeId) { toast('請選擇假別', 'error'); return; }
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api('PATCH', `/leave/balances/${initial.id}`, {
          entitled_days: entitledNum,
          adjusted_days: adjustedNum,
        });
        toast('額度已更新', 'ok');
      } else {
        await api('POST', '/leave/balances', {
          employee_id: employeeId,
          leave_type_id: leaveTypeId,
          year: Number(year),
          entitled_days: entitledNum,
          adjusted_days: adjustedNum,
          used_days: 0,
          pending_days: 0,
        });
        toast('額度已建立', 'ok');
      }
      onSaved();
    } catch (e) {
      toast('儲存失敗：' + (e.detail || e.message || ''), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={isEdit ? `編輯額度 · ${initial.employee_name} · ${initial.leave_type_name}` : '新增額度'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting ? '儲存中…' : (isEdit ? '更新' : '建立')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="員工" span={isEdit ? 1 : 2}>
          {isEdit ? (
            <div style={uiStyle({ padding: '8px 12px', background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.xs }, { padding: '10px 12px', background: GxT.color.s2, borderRadius: GxT.radius.input })}>
              {initial.employee_name} <span style={{ color: HR.inkFaint }}>#{initial.employee_id}</span>
            </div>
          ) : (
            <select value={employeeId || ''} onChange={(e) => setEmployeeId(Number(e.target.value))} style={inputStyle()}>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.name}{e.employee_no ? ` · ${e.employee_no}` : ''}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="假別">
          {isEdit ? (
            <div style={uiStyle({ padding: '8px 12px', background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.xs }, { padding: '10px 12px', background: GxT.color.s2, borderRadius: GxT.radius.input })}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: leaveColor(initial.leave_type_code), border: `1px solid ${HR.rule}` }} />
                {initial.leave_type_name}
              </span>
            </div>
          ) : (
            <select value={leaveTypeId || ''} onChange={(e) => setLeaveTypeId(Number(e.target.value))} style={inputStyle()}>
              {types.filter(t => t.active && t.quota_managed).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="年份">
          {isEdit ? (
            <div style={uiStyle({ padding: '8px 12px', background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.xs }, { padding: '10px 12px', background: GxT.color.s2, borderRadius: GxT.radius.input })}>{initial.year}</div>
          ) : (
            <TextInput value={String(year)} onChange={(v) => setYear(v)} placeholder="2026" />
          )}
        </Field>
        <Field label="已給天數 (entitled)">
          <TextInput value={entitled} onChange={setEntitled} placeholder="14" />
        </Field>
        <Field label="調整天數 (adjusted, 可負)">
          <TextInput value={adjusted} onChange={setAdjusted} placeholder="0" />
        </Field>
        {isEdit && (
          <Field label="已用 / 待審 (自動)" span={2}>
            <div style={{ display: 'flex', gap: 12, fontSize: FontSizes.xxs, color: HR.inkSoft, padding: '4px 0' }}>
              <span>已用：<strong style={{ color: HR.ink }}>{fmtDays(initial.used_days)}</strong> 天</span>
              <span>待審：<strong style={{ color: HR.ink }}>{fmtDays(initial.pending_days)}</strong> 天</span>
              <span style={{ marginLeft: 'auto', color: HR.inkFaint }}>
                可用：<strong style={{ color: HR.accent }}>{initial.available_days}</strong> 天
              </span>
            </div>
          </Field>
        )}
      </div>
    </Modal>
  );
}

/* Batch-grant modal — apply the same entitled_days to N employees in one go.
 * Year-start workflow: HR picks 特休 + 2026 + 14 days, hits 發放, frontend
 * loops POST /leave/balances per employee (backend has no batch endpoint).
 *
 * Safety:
 *   • Default: skip employees who already have a balance for (type, year).
 *     Avoids accidentally wiping out per-employee adjustments.
 *   • Opt-in: 「覆寫已存在的紀錄」checkbox switches existing rows to PATCH.
 *   • Live preview shows: 將發放 X 筆 / 跳過 Y 筆 / 將覆寫 Z 筆 before submit.
 *   • Progress bar during loop; per-row error log on completion. */
function BatchGrantModal({ types, employees, balances, defaultYear, onClose, onSaved }) {
  const toast = useToast();
  const activeTypes = types.filter(t => t.active && t.quota_managed);
  // Default to 特休/annual if it exists; otherwise first active type
  const annualType = activeTypes.find(t => t.code === 'annual') || activeTypes[0];
  const [leaveTypeId, setLeaveTypeId] = React.useState(annualType?.id || null);
  const [year, setYear] = React.useState(defaultYear || new Date().getFullYear());
  const [entitled, setEntitled] = React.useState('14');
  const [overwrite, setOverwrite] = React.useState(false);
  const [progress, setProgress] = React.useState(null);  // null | {done, total, errors}

  // Set of employee_ids that already have a balance for this (type, year)
  const existingIds = new Set(
    balances
      .filter(b => b.leave_type_id === leaveTypeId && b.year === Number(year))
      .map(b => b.employee_id)
  );
  const newGrants = employees.filter(e => !existingIds.has(e.id));
  const conflicts = employees.filter(e =>  existingIds.has(e.id));
  // Targets to write: new grants always; conflicts only when overwrite checked
  const targets = overwrite ? employees : newGrants;
  const willCreate = newGrants.length;
  const willUpdate = overwrite ? conflicts.length : 0;
  const willSkip   = overwrite ? 0 : conflicts.length;

  const submit = async () => {
    const entitledNum = parseFloat(entitled);
    if (!Number.isFinite(entitledNum) || entitledNum < 0) { toast('天數需為 ≥ 0 的數字', 'error'); return; }
    if (!leaveTypeId) { toast('請選擇假別', 'error'); return; }
    if (targets.length === 0) { toast('沒有可發放的對象', 'error'); return; }
    if (!confirm(`確定要發放 ${entitledNum} 天 給 ${targets.length} 名員工？`)) return;

    const errors = [];
    setProgress({ done: 0, total: targets.length, errors: [] });
    for (let i = 0; i < targets.length; i++) {
      const emp = targets[i];
      try {
        const existing = balances.find(b =>
          b.employee_id === emp.id && b.leave_type_id === leaveTypeId && b.year === Number(year)
        );
        if (existing && overwrite) {
          await api('PATCH', `/leave/balances/${existing.id}`, { entitled_days: entitledNum });
        } else if (!existing) {
          await api('POST', '/leave/balances', {
            employee_id: emp.id,
            leave_type_id: leaveTypeId,
            year: Number(year),
            entitled_days: entitledNum,
            adjusted_days: 0,
            used_days: 0,
            pending_days: 0,
          });
        }
      } catch (e) {
        errors.push({ emp: emp.name, msg: e.detail || e.message || 'unknown' });
      }
      setProgress({ done: i + 1, total: targets.length, errors: [...errors] });
    }
    if (errors.length === 0) {
      toast(`完成：${targets.length} 筆已發放`, 'ok');
      onSaved();
    } else {
      toast(`完成：成功 ${targets.length - errors.length} / 失敗 ${errors.length}`, 'warn');
      // Stay open so HR can review errors. They close manually.
    }
  };

  const running = progress && progress.done < progress.total;
  const done    = progress && progress.done === progress.total;
  const typeName = types.find(t => t.id === leaveTypeId)?.name || '(未選)';
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  return (
    <Modal
      open onClose={running ? undefined : onClose}
      title="批次發放額度"
      subtitle="Batch Grant"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={running}>
            {done ? '關閉' : '取消'}
          </Button>
          {!done && (
            <Button variant="primary" onClick={submit} disabled={running || targets.length === 0}>
              {running ? `處理中… ${progress.done}/${progress.total}` : `發放給 ${targets.length} 名員工`}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="假別">
          <select value={leaveTypeId || ''} onChange={(e) => setLeaveTypeId(Number(e.target.value))} style={inputStyle()} disabled={!!progress}>
            {activeTypes.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
        </Field>
        <Field label="年份">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={inputStyle()} disabled={!!progress}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Field>
        <Field label="每人發放天數" span={2}>
          <TextInput value={entitled} onChange={setEntitled} placeholder="14" disabled={!!progress} />
        </Field>
        <Field label="覆寫已存在的紀錄" span={2} hint="預設只發放給「沒有此紀錄」的員工。勾選後會用 PATCH 蓋掉既有 entitled_days（已用 / 已調整不動）。">
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: FontSizes.xs, color: HR.ink, cursor: progress ? 'not-allowed' : 'pointer' }}>
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              disabled={!!progress}
            />
            <span>是 — 覆寫 {conflicts.length} 名既有員工的 entitled_days</span>
          </label>
        </Field>
      </div>

      {/* 即時預覽：誰會被影響 */}
      <div style={uiStyle({ marginTop: 18, padding: '14px 16px', background: HR.surfaceAlt, borderRadius: 4, border: `1px solid ${HR.rule}` }, { background: GxT.color.s2, borderRadius: GxT.radius.inner, border: 'none' })}>
        <div style={_lvGxLabel({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 })}>
          影響預覽 · {typeName} · {year}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: FontSizes.xs }}>
          <div>
            <div style={{ fontSize: FontSizes.trading_lg, color: uiPick(HR.inkFaint, GxT.color.onV) }}>新發放</div>
            <div style={{ fontSize: FontSizes.lg, fontFamily: HR.mono, fontWeight: uiPick(600, 800), color: HR.accent }}>{willCreate}</div>
          </div>
          <div>
            <div style={{ fontSize: FontSizes.trading_lg, color: uiPick(HR.inkFaint, GxT.color.onV) }}>覆寫</div>
            <div style={{ fontSize: FontSizes.lg, fontFamily: HR.mono, fontWeight: uiPick(600, 800), color: willUpdate > 0 ? HR.warn : HR.inkFaint }}>{willUpdate}</div>
          </div>
          <div>
            <div style={{ fontSize: FontSizes.trading_lg, color: uiPick(HR.inkFaint, GxT.color.onV) }}>跳過</div>
            <div style={{ fontSize: FontSizes.lg, fontFamily: HR.mono, fontWeight: uiPick(600, 800), color: HR.inkFaint }}>{willSkip}</div>
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }}>
          共 {employees.length} 名員工 · 將寫入 {targets.length} 筆
        </div>
      </div>

      {/* 進度條 + 錯誤列表 */}
      {progress && (
        <div style={uiStyle({ marginTop: 16, padding: '14px 16px', background: '#fff', borderRadius: 4, border: `1px solid ${HR.rule}` }, { borderRadius: GxT.radius.inner })}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={_lvGxLabel({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase' })}>
              {done ? '已完成' : '處理中'}
            </span>
            <span style={{ fontSize: FontSizes.xxs, fontFamily: HR.mono, color: HR.ink }}>
              {progress.done} / {progress.total}
            </span>
          </div>
          <div style={_lvGxTrack({ height: 6, background: HR.ruleSoft, borderRadius: 3, overflow: 'hidden' })}>
            <div style={{
              height: '100%', width: `${(progress.done / progress.total) * 100}%`,
              background: progress.errors.length > 0 ? HR.warn : HR.accent,
              transition: 'width 200ms',
            }}/>
          </div>
          {progress.errors.length > 0 && (
            <div style={{ marginTop: 10, fontSize: FontSizes.xxs }}>
              <div style={{ color: HR.down, fontWeight: 500, marginBottom: 4 }}>失敗 {progress.errors.length} 筆：</div>
              <div style={{ maxHeight: 120, overflowY: 'auto', fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkSoft }}>
                {progress.errors.map((er, i) => (
                  <div key={i}>{er.emp}：{er.msg}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function BalancesTab({ types, defaultYear }) {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = React.useState(defaultYear || currentYear);
  const [balances, setBalances] = React.useState(null);  // null = loading
  const [employees, setEmployees] = React.useState([]);
  const [editing, setEditing] = React.useState(null);     // null=closed, {}=new, row=edit
  const [batchOpen, setBatchOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filterTypeId, setFilterTypeId] = React.useState('');
  const [recomputingEmpId, setRecomputingEmpId] = React.useState(null);

  const load = React.useCallback(async () => {
    setBalances(null);
    try {
      const [bal, empResp] = await Promise.all([
        api('GET', `/leave/balances?year=${year}`),
        // /employees（清單）對 HR 關閉（避免開放員工/薪資模組），改打 /attendance/roster：
        // HR 已可存取，回傳「陣列」（每筆 id/name/emp_code/position/department_*），非分頁物件。
        api('GET', '/attendance/roster').catch(() => []),
      ]);
      setBalances(Array.isArray(bal) ? bal : []);
      // roster 回傳陣列；保險起見也容忍分頁 { items: [...] } 形狀。
      const empList = Array.isArray(empResp) ? empResp : (empResp.items || []);
      // 後續 UI 以 employee_no 顯示員編後綴，roster 欄位名為 emp_code → 對齊。
      setEmployees(empList.map(e => ({ ...e, employee_no: e.employee_no ?? e.emp_code })));
    } catch (e) {
      toast('額度載入失敗：' + (e.detail || e.message || ''), 'error');
      setBalances([]);
    }
  }, [year]);

  React.useEffect(() => { load(); }, [load]);

  const recomputeEmployee = async (empId) => {
    setRecomputingEmpId(empId);
    try {
      const r = await api('POST', '/leave/balances/recompute', { employee_id: empId, year });
      toast(`已重算 ${r.types_processed} 種假別 · ${r.rows_updated} 筆更新`, 'ok');
      await load();
    } catch (e) {
      toast('重算失敗：' + (e.detail || e.message || ''), 'error');
    } finally {
      setRecomputingEmpId(null);
    }
  };

  const filtered = (balances || []).filter(b => {
    const lt = types.find(t => t.id === b.leave_type_id);
    if (!lt || !lt.quota_managed) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(b.employee_name || '').toLowerCase().includes(q)) return false;
    }
    if (filterTypeId && b.leave_type_id !== Number(filterTypeId)) return false;
    return true;
  });

  // 年份選單：前 2 年 + 今年 + 下年
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  return (
    <>
      <Card className="hr-leave-balances-card" padding="0" eyebrow="Leave Balances" title="員工請假額度"
        italic={`${filtered.length} 筆 · ${year}`}
        right={
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <Button variant="secondary" size="md" icon="plus" onClick={() => setBatchOpen(true)} disabled={employees.length === 0}>批次發放</Button>
            <Button variant="primary"   size="md" icon="plus" onClick={() => setEditing({})}>新增額度</Button>
          </div>
        }
      >
        {/* Toolbar：年份 + 搜尋 + 假別過濾 */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '14px 24px', borderBottom: `1px solid ${HR.ruleSoft}`, background: HR.surfaceAlt,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={_lvGxLabel({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' })}>年份</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={inputStyle({ padding: '6px 11px', width: 100, fontSize: FontSizes.trading_md })}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={_lvGxLabel({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' })}>假別</span>
            <select value={filterTypeId} onChange={(e) => setFilterTypeId(e.target.value)} style={inputStyle({ padding: '6px 11px', width: 130, fontSize: FontSizes.trading_md })}>
              <option value="">全部</option>
              {types.filter(t => t.active && t.quota_managed).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
            <span style={_lvGxLabel({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' })}>搜尋</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="員工姓名"
              style={inputStyle({ padding: '6px 11px', fontSize: FontSizes.trading_md, flex: 1, maxWidth: 220 })}
            />
          </div>
        </div>

        {balances === null ? (
          <SkeletonTable rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon="dollar-sign" title={(balances || []).length === 0 ? `${year} 年尚無額度紀錄` : '找不到符合條件的紀錄'}
            hint={(balances || []).length === 0 ? '點右上角新增。' : '試試清空篩選條件。'} />
        ) : (
          <div className="hr-leave-balances-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>員工</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>假別</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>已給</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>調整</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>已用</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>待審</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>可用</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => {
                const lowAvailable = (b.available_days ?? 0) <= 0;
                const noQuota = b.quota_managed === false;  // 事假/病假類
                const dash = <span style={{ color: HR.inkFaint }}>—</span>;
                return (
                  <tr key={b.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                    <td style={{ padding: '13px 16px 13px 24px', fontWeight: 500 }}>
                      {b.employee_name}
                      <span style={{ marginLeft: 6, fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }}>#{b.employee_id}</span>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: leaveColor(b.leave_type_code), border: `1px solid ${HR.rule}` }} />
                        {b.leave_type_name}
                        {noQuota && <span style={_lvGxUpright({ fontSize: FontSizes.trading_sm, color: HR.inkFaint, fontStyle: 'italic' })}>· 非配額制</span>}
                      </span>
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums' }}>{noQuota ? dash : fmtDays(b.entitled_days)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums', color: b.adjusted_days < 0 ? HR.down : (b.adjusted_days > 0 ? HR.accent : HR.inkSoft) }}>
                      {noQuota ? dash : (<>{b.adjusted_days > 0 ? '+' : ''}{fmtDays(b.adjusted_days)}</>)}
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums', color: HR.inkSoft }}>{fmtDays(b.used_days)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums', color: b.pending_days > 0 ? HR.warn : HR.inkFaint }}>{fmtDays(b.pending_days)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: lowAvailable ? HR.down : HR.accent }}>{noQuota ? dash : fmtDays(b.available_days)}</td>
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <Button size="sm" variant="ghost" onClick={() => recomputeEmployee(b.employee_id)} disabled={recomputingEmpId === b.employee_id}
                          title="從 leave_requests 重算 used / pending（不動 entitled / adjusted）">
                          {recomputingEmpId === b.employee_id ? '重算中…' : '重算'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(b)}>編輯</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Card>
      {editing !== null && (
        <BalanceForm
          initial={editing}
          types={types}
          employees={employees}
          defaultYear={year}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {batchOpen && (
        <BatchGrantModal
          types={types}
          employees={employees}
          balances={balances || []}
          defaultYear={year}
          onClose={() => setBatchOpen(false)}
          onSaved={() => { setBatchOpen(false); load(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  Page entry  ─────────────────────── */

/* HR/admin 用：抓全公司請假單（後端對 hr/admin 不帶 employee_id 即回全部）。
 * 分頁掃到底，避免資料量超過單頁上限 200 就漏看。 */
async function fetchAllLeaveRequests() {
  const pageSize = 200;
  const items = [];
  for (let page = 1; page <= 10; page += 1) {   // 安全上限 2000 筆
    const resp = await api('GET', `/leave/requests?page=${page}&page_size=${pageSize}`);
    const batch = resp.items || [];
    items.push(...batch);
    if (batch.length < pageSize || items.length >= (resp.total || 0)) break;
  }
  return items;
}

/* 員工本人用：分頁掃到底抓自己全部的申請（A1 按月分組要保證所有紀錄找得到，
 * 不能只抓前 100 筆）。單頁上限 200、安全上限 10 頁（2000 筆）。 */
async function fetchAllMyLeaveRequests() {
  const pageSize = 200;
  const items = [];
  for (let page = 1; page <= 10; page += 1) {
    const resp = await api('GET', `/leave/requests/me?page=${page}&page_size=${pageSize}`);
    const batch = resp.items || [];
    items.push(...batch);
    if (batch.length < pageSize || items.length >= (resp.total || 0)) break;
  }
  return items;
}

function LeavePage() {
  const [user, setUser] = React.useState(null);
  const [selfEmp, setSelfEmp] = React.useState(null);
  const [types, setTypes] = React.useState([]);
  const [myRequests, setMyRequests] = React.useState([]);
  const [myBalances, setMyBalances] = React.useState([]);
  const [pendingRequests, setPendingRequests] = React.useState([]);
  const [allRequests, setAllRequests] = React.useState([]);
  const [tab, setTab] = React.useState('me');
  const [loading, setLoading] = React.useState(true);

  // Capability-driven visibility: approver tab needs leave.approve, types/balances need leave.type.manage
  const canApprove = hasCapability(user, 'leave.approve');
  const canManageTypes = hasCapability(user, 'leave.type.manage');
  // 額度管理跟假別管理用同一個 capability — 都是 HR/admin 才能動的設定
  const canManageBalances = canManageTypes;
  // 審核歷程看全公司：對齊後端 GET /leave/requests 的 is_hr_or_admin（hr + admin）
  const isLeaveManager = isAttendanceManager(user);
  const isHR = canApprove || canManageTypes;  // 任一即顯示 HR-flavoured headline
  const year = new Date().getFullYear();

  const refresh = React.useCallback(async (u) => {
    const who = u || user;
    const whoCanManageTypes = hasCapability(who, 'leave.type.manage');
    const whoCanApprove = hasCapability(who, 'leave.approve');
    const [myReqs, balResp, typesResp] = await Promise.all([
      fetchAllMyLeaveRequests().catch(() => []),
      api('GET', `/leave/balances/me?year=${year}`).catch(() => []),
      // 能管假別的人才看得到停用的；其他人只看啟用
      api('GET', `/leave/types?active_only=${whoCanManageTypes ? 'false' : 'true'}`).catch(() => []),
    ]);
    setMyRequests(Array.isArray(myReqs) ? myReqs : []);
    setMyBalances(Array.isArray(balResp) ? balResp : []);
    setTypes(Array.isArray(typesResp) ? typesResp : []);

    if (whoCanApprove) {
      const pendResp = await api('GET', '/leave/requests?status=pending&page_size=100').catch(() => ({ items: [] }));
      setPendingRequests(pendResp.items || []);
    } else {
      setPendingRequests([]);
    }

    // hr/admin 的審核歷程分頁要看全公司歷程，不只自己的
    if (isAttendanceManager(who)) {
      setAllRequests(await fetchAllLeaveRequests().catch(() => []));
    } else {
      setAllRequests([]);
    }
  }, [year, user]);

  React.useEffect(() => {
    let alive = true;
    Promise.all([
      api('GET', '/auth/me').catch(() => null),
      api('GET', '/employees/me').catch(() => null),
    ]).then(async ([u, emp]) => {
      if (!alive) return;
      setUser(u);
      setSelfEmp(emp);
      if (u) await refresh(u);
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, []);  // first load only

  const pendingCount = pendingRequests.length;
  const tabs = [
    { key: 'me',      label: '我的申請', icon: 'calendar' },
    { key: 'history', label: '審核歷程', icon: 'clock' },
    ...(canApprove ? [
      { key: 'approvals', label: '待審核', icon: 'arrow-right', count: pendingCount },
    ] : []),
    ...(canManageBalances ? [
      { key: 'balances',  label: '額度管理', icon: 'dollar-sign' },
    ] : []),
    ...(canManageTypes ? [
      { key: 'types',     label: '假別管理', icon: 'settings' },
    ] : []),
  ];

  return (
    <>
      <PageHeader
        eyebrow="出勤管理 · Leave & Attendance"
        title="假勤申請"
        italic={isHR ? 'Leave & Attendance Console' : 'My Requests'}
      />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      {loading ? (
        <SkeletonTable rows={6} />
      ) : (
        <>
          {tab === 'me'        && <MyLeaveTab    types={types} myBalances={myBalances} myRequests={myRequests} selfEmpId={selfEmp?.id} onRefresh={() => refresh()} />}
          {tab === 'history'   && <HistoryTab    requests={isLeaveManager ? allRequests : myRequests} showEmployee={isLeaveManager} types={types} />}
          {tab === 'approvals' && <ApprovalsTab  pendingRequests={pendingRequests} onRefresh={() => refresh()} />}
          {tab === 'balances'  && <BalancesTab   types={types} defaultYear={year} />}
          {tab === 'types'     && <TypesTab      types={types} onRefresh={() => refresh()} />}
        </>
      )}
    </>
  );
}

/* ─────────────────────────  HistoryTab  ─────────────────────────
 * 顯示請假單的完整審核流程（approvals[]）— timeline 樣式。
 * 一般員工只看自己的；hr/admin 看全公司（showEmployee 時卡片顯示申請人）。
 * 後端 LeaveRequestResponse.approvals 已回 step_no/approver_name/status/decided_at/comment。
 */

function HistoryTab({ requests, types, showEmployee }) {
  const typeName = (id) => types.find(t => t.id === id)?.name || '未知';
  const sorted = React.useMemo(() => [...(requests || [])].sort((a, b) =>
    (b.submitted_at || b.created_at || '').localeCompare(a.submitted_at || a.created_at || '')
  ), [requests]);

  // 各狀態筆數（給統計 + 篩選按鈕用）
  const counts = React.useMemo(() => {
    const c = { all: sorted.length, pending: 0, approved: 0, rejected: 0, cancelled: 0 };
    for (const r of sorted) {
      if (r.status in c) c[r.status] += 1;
    }
    return c;
  }, [sorted]);

  const [filter, setFilter] = React.useState('all');  // all | pending | approved | rejected | cancelled
  const visible = filter === 'all' ? sorted : sorted.filter(r => r.status === filter);

  if (sorted.length === 0) {
    return <EmptyState icon="clock" title="尚無申請紀錄" hint="送出第一張申請（請假 / 加班 / 補班）後會顯示在這裡" />;
  }

  // 篩選 chip 配置：key / label / tone / count
  const chipDefs = [
    { key: 'all',       label: '全部',   tone: 'ink',    count: counts.all },
    { key: 'pending',   label: '審核中', tone: 'warn',   count: counts.pending },
    { key: 'approved',  label: '已通過', tone: 'accent', count: counts.approved },
    { key: 'rejected',  label: '已駁回', tone: 'down',   count: counts.rejected },
    { key: 'cancelled', label: '已取消', tone: 'mute',   count: counts.cancelled },
  ];
  const toneColor = uiPick({
    ink:    { active: HR.ink,    bg: '#F3F1ED', text: HR.ink     },
    warn:   { active: HR.warn,   bg: '#FBF1DE', text: '#8A5A1A'  },
    accent: { active: HR.accent, bg: '#E8F1EE', text: '#0B5F5A'  },
    down:   { active: HR.down,   bg: '#FAEFEF', text: HR.down    },
    mute:   { active: HR.inkSoft, bg: HR.surfaceAlt, text: HR.inkSoft },
  }, {
    // GX: GxT tints (all ≥ 4.5:1); selected = solid tone with white label
    ink:    { active: GxT.color.inv,     bg: GxT.tint.neutral.bg, text: GxT.color.on },
    warn:   { active: GxT.tint.warn.fg,  bg: GxT.tint.warn.bg,    text: GxT.tint.warn.fg },
    accent: { active: GxT.color.p,       bg: GxT.tint.ok.bg,      text: GxT.tint.ok.fg },
    down:   { active: GxT.color.down,    bg: GxT.tint.down.bg,    text: GxT.tint.down.fg },
    mute:   { active: GxT.color.onV,     bg: GxT.tint.neutral.bg, text: GxT.tint.neutral.fg },
  });

  return (
    <div>
      {/* 頂部：狀態統計 + 篩選 chip */}
      <div style={uiStyle({
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
        padding: '10px 14px', marginBottom: 14,
        background: HR.surface, border: `1px solid ${HR.ruleSoft}`, borderRadius: 6,
      }, { padding: '12px 14px', border: 'none', borderRadius: GxT.radius.card })}>
        {chipDefs.map(c => {
          const active = filter === c.key;
          const tc = toneColor[c.tone];
          const disabled = c.count === 0 && c.key !== 'all';
          return (
            <button
              key={c.key}
              onClick={() => !disabled && setFilter(c.key)}
              disabled={disabled}
              aria-pressed={uiPick(undefined, active)}
              style={uiStyle({
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 999,
                fontSize: FontSizes.xxs, fontWeight: 500,
                border: `1px solid ${active ? tc.active : HR.ruleSoft}`,
                background: active ? tc.active : (disabled ? 'transparent' : tc.bg),
                color: active ? '#fff' : (disabled ? HR.inkFaint : tc.text),
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'background 120ms, border 120ms, color 120ms',
              }, {
                minHeight: 32, padding: '0 12px', borderRadius: GxT.radius.chip, fontWeight: 700,
                border: `1px solid ${active ? tc.active : (disabled ? GxT.color.olv : 'transparent')}`,
                fontFamily: GxT.font,
              })}
            >
              {c.label}
              <span style={uiStyle({
                fontFamily: HR.mono, fontSize: FontSizes.trading_lg, fontWeight: 600,
                padding: '0 6px', borderRadius: 999,
                background: active ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.05)',
              }, { fontFamily: GxT.font, fontWeight: 800, borderRadius: GxT.radius.chipSm, fontVariantNumeric: 'tabular-nums' })}>{c.count}</span>
            </button>
          );
        })}
      </div>

      {/* 篩選後若無資料 */}
      {visible.length === 0 ? (
        <div style={uiStyle({
          padding: '40px 20px', textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs,
          background: HR.surface, border: `1px dashed ${HR.ruleSoft}`, borderRadius: 6,
        }, { border: 'none', borderRadius: GxT.radius.card, color: GxT.color.onV })}>
          此狀態下無請假紀錄
        </div>
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {visible.map(req => (
        <HistoryCard
          key={req.id}
          req={req}
          typeName={typeName}
          leaveTypeCode={req.leave_type_code}
          showEmployee={showEmployee}
        />
      ))}
      </div>
      )}
    </div>
  );
}

/* ─── HistoryCard（方案 B · Status Banner Hero） ───────────────────
 * 頂部全寬狀態 banner：一眼看出結果 + 決策者 + 相對時間
 * Body：假別 chip + 事由節錄 + 天數 + 期間
 * Reason quote：serif italic 引言框，駁回時顯示 decision_remark
 */
// Lazy (was a module-level const that copied HR.inkFaint at eval time — plan §2(d)-2): built per
// render so a router fallback (GxCore.restore) can never leave GX values behind. Bento result is
// deep-equal to the former _BANNER_STYLES literal.
/* gx-lazy:_lvBannerStyles:start */
function _lvBannerStyles() {
  return uiPick({
    approved:  { bg: '#E8F1EE',   color: '#0B5F5A' },
    rejected:  { bg: '#FAEFEF',   color: HR.down  },
    pending:   { bg: '#FBF1DE',   color: '#8A5A1A' },
    cancelled: { bg: HR.surfaceAlt, color: HR.inkFaint },
  }, {
    approved:  { bg: GxT.tint.ok.bg,      color: GxT.tint.ok.fg },
    rejected:  { bg: GxT.tint.down.bg,    color: GxT.tint.down.fg },
    pending:   { bg: GxT.tint.warn.bg,    color: GxT.tint.warn.fg },
    cancelled: { bg: GxT.tint.neutral.bg, color: GxT.tint.neutral.fg },
  });
}
/* gx-lazy:_lvBannerStyles:end */

function HistoryCard({ req, typeName, leaveTypeCode, showEmployee }) {
  const status = req.status || 'pending';
  const bannerStyles = _lvBannerStyles();
  const banner = bannerStyles[status] || bannerStyles.pending;

  // 期間：單日不重複
  const sDate = (req.start_at || '').slice(0, 10);
  const eDate = (req.end_at || '').slice(0, 10);
  const periodStr = sDate === eDate ? sDate : `${sDate} → ${eDate}`;

  // 決策者 / 下一位待簽核者
  const chain = Array.isArray(req.approvals) ? req.approvals : [];
  const pendingStep = chain.find(s => s.status === 'pending');
  const lastDecided = [...chain].reverse().find(s => s.status === 'approved' || s.status === 'rejected');

  let bannerText = '';
  if (status === 'pending') {
    const waiting = pendingStep?.approver_name;
    bannerText = waiting ? `審核中 · 等待 ${waiting} 簽核` : '審核中 · 等待 HR/主管 處理';
  } else if (status === 'approved') {
    const who = req.decided_by_name || lastDecided?.approver_name || '主管';
    const when = req.decided_at ? fmtShortLocal(req.decided_at) : '';
    bannerText = `已通過${when ? ` · ${who} ${when} 批准` : ` · ${who} 批准`}`;
  } else if (status === 'rejected') {
    const who = req.decided_by_name || lastDecided?.approver_name || '主管';
    const when = req.decided_at ? fmtShortLocal(req.decided_at) : '';
    bannerText = `已駁回${when ? ` · ${who} ${when}` : ` · ${who}`}`;
  } else if (status === 'cancelled') {
    bannerText = '已取消';
  }

  // 相對時間：pending 用 submitted_at，已決狀態用 decided_at
  const refIso = (status === 'pending')
    ? (req.submitted_at || req.created_at)
    : (req.decided_at || req.updated_at || req.created_at);
  const relText = (status === 'pending')
    ? (refIso ? `送出 ${fmtRelativeTime(refIso)}` : '')
    : (refIso ? fmtRelativeTime(refIso) : '');

  // 駁回理由 / 申請事由
  const rejectionRemark = (status === 'rejected')
    ? (req.decision_remark || lastDecided?.comment || '')
    : '';

  const lvCode = leaveTypeCode || req.leave_type_code;
  const lvColor = leaveColor(lvCode);

  return (
    <div style={uiStyle({
      background: HR.surface,
      border: `1px solid ${HR.ruleSoft}`,
      borderRadius: 6,
      overflow: 'hidden',
    }, { border: 'none', borderRadius: GxT.radius.card })}>
      {/* 頂部狀態 banner */}
      <div style={uiStyle({
        padding: '9px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: FontSizes.xxs, fontWeight: 600,
        background: banner.bg, color: banner.color,
      }, { padding: '10px 18px', fontWeight: 700 })}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: 'currentColor',
          animation: status === 'pending' ? 'lv-pulse 1.6s ease-in-out infinite' : 'none',
        }} />
        <span>{bannerText}</span>
        {relText && (
          <span style={{
            marginLeft: 'auto', fontWeight: 400,
            fontFamily: HR.mono, fontSize: FontSizes.trading_lg, opacity: 0.85,
          }}>{relText}</span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '14px 18px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
          flexWrap: 'wrap',
        }}>
          {showEmployee && req.employee_name && (
            <span style={{
              fontSize: FontSizes.xs, fontWeight: 600, color: HR.ink,
            }}>{req.employee_name}</span>
          )}
          <span style={uiStyle({
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 3,
            fontSize: FontSizes.trading_lg, fontWeight: 600,
            background: GxCore.alpha(lvColor, '22'), color: lvColor,
          }, { borderRadius: GxT.radius.chipSm, fontWeight: 700 })}>
            <span style={{ width: 5, height: 5, borderRadius: uiPick(1, '50%'), background: lvColor }} />
            {typeName(req.leave_type_id)}
          </span>
          <span style={uiStyle({
            fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft,
            padding: '1px 8px', background: HR.surfaceAlt, borderRadius: 3,
          }, { padding: '3px 10px', background: GxT.color.s2, borderRadius: GxT.radius.chipSm, fontWeight: 700 })}>{fmtDays(req.duration_days)} 天</span>
          <span style={{
            marginLeft: 'auto',
            fontFamily: HR.mono, fontSize: FontSizes.trading_sm, color: HR.inkFaint,
          }}>{req.request_no || ''}</span>
        </div>
        <div style={{
          fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft, marginBottom: req.reason || rejectionRemark ? 10 : 0,
        }}>{periodStr}</div>

        {/* 駁回理由優先；否則顯示申請事由（pending / approved） */}
        {rejectionRemark ? (
          <div style={uiStyle({
            fontStyle: 'italic', fontFamily: HR.serif, fontSize: FontSizes.xs,
            color: HR.down,
            padding: '8px 12px', borderLeft: `3px solid ${HR.down}`,
            background: '#FAEFEF', borderRadius: '0 4px 4px 0',
          }, {
            fontStyle: 'normal', color: GxT.tint.down.fg,
            padding: '10px 14px', borderLeft: 'none', background: GxT.tint.down.bg, borderRadius: GxT.radius.inner,
          })}>
            <span style={{ fontStyle: 'normal', fontFamily: 'inherit', fontSize: FontSizes.trading_lg, fontWeight: 600, color: HR.down, marginRight: 6 }}>駁回理由</span>
            {rejectionRemark}
          </div>
        ) : req.reason ? (
          <div style={uiStyle({
            fontStyle: 'italic', fontFamily: HR.serif, fontSize: FontSizes.xs,
            color: HR.inkSoft,
            padding: '6px 12px', borderLeft: `2px solid ${HR.ruleSoft}`,
            background: HR.surfaceAlt, borderRadius: '0 4px 4px 0',
          }, {
            fontStyle: 'normal', padding: '10px 14px', borderLeft: 'none',
            background: GxT.color.s2, borderRadius: GxT.radius.inner,
          })}>
            <span style={{ fontStyle: 'normal', fontFamily: 'inherit', fontSize: FontSizes.trading_lg, fontWeight: uiPick(600, 700), color: uiPick(HR.inkFaint, GxT.color.onV), marginRight: 6 }}>事由</span>
            {req.reason}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// inject pulse keyframes (idempotent — 一次性掛到 <head>)
(function injectLeavePulseCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('lv-pulse-css')) return;
  const s = document.createElement('style');
  s.id = 'lv-pulse-css';
  s.textContent = `@keyframes lv-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.8); } }`;
  document.head.appendChild(s);
})();

// Expressive (GX) page CSS — only inserted when <html data-ui-shell="expressive"> (GxCore.injectCss
// is a no-op under Bento; router fallback removes every style[id^="gx-css-"]). Card h3 / Card
// header+body paddings are inline styles, hence !important. Plan §2(c) "頁面專屬 CSS".
GxCore.injectCss('gx-css-p-leave', String.raw`
html[data-ui-shell="expressive"] .gx-main .hr-leave-balance-grid > div h3{font-size:30px!important;font-weight:900!important;line-height:1.15!important;letter-spacing:-.5px!important;font-variant-numeric:tabular-nums}
html[data-ui-shell="expressive"] .gx-main .hr-leave-balance-grid > div h3 > span{font-size:13px!important;font-weight:700!important;letter-spacing:0!important;color:var(--gx-on-v)!important;font-variant-numeric:tabular-nums}
@media (max-width:767px){
html[data-ui-shell="expressive"] .gx-main .hr-leave-balance-grid > div > div:first-child{padding:14px 14px 0!important}
html[data-ui-shell="expressive"] .gx-main .hr-leave-balance-grid > div > div:last-child{padding:8px 14px 14px!important}
html[data-ui-shell="expressive"] .gx-main .hr-leave-balance-grid > div h3{font-size:24px!important}
html[data-ui-shell="expressive"] .gx-main .hr-leave-balance-grid > div h3 > span{display:block;font-size:12px!important;margin-top:2px}
}
`);

window.LeavePage = LeavePage;
window.leaveColor = leaveColor;
